'use client'

import { Download, Loader2 } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { track } from '@/lib/analytics'
import { stateDisplayName } from '@/lib/constants'
import { canonicalUrl } from '@/lib/flags'
import type { FuelEstimate } from '@/lib/types'
import { type AuxPowerLine, useAuxPowerLine } from '@/stores/use-aux-power-line'

interface PdfExportProps {
	estimate: FuelEstimate
	originName: string
	destinationName: string
}

/**
 * Max coordinates we send to /api/static-map. A full Mapbox `overview=full`
 * route geometry can be ENORMOUS — a cross-country tour import clocks ~80k
 * points, a long 12-stop tour far more. The static-map endpoint caps input
 * size (and downsamples to a ~200-point thumbnail anyway), so shipping the raw
 * geometry both 400s the request and wastes ~2.5MB of bandwidth. We
 * pre-downsample to a small, evenly-spaced subset here — ample for a thumbnail.
 */
const MAP_THUMB_POINTS = 1000

/**
 * Evenly downsample a coordinate list to at most `max` points, always keeping
 * the first and last (mirrors the server-side downsample so the line is smooth
 * end-to-end).
 */
function downsampleCoords(coords: number[][], max: number): number[][] {
	if (coords.length <= max) return coords
	const out: number[][] = []
	const step = (coords.length - 1) / (max - 1)
	for (let i = 0; i < max; i++) {
		const point = coords[Math.round(i * step)]
		if (point) out.push(point)
	}
	return out
}

/**
 * Fetch a rendered route map image from our `/api/static-map` proxy and
 * convert to a data URL jsPDF can embed. Returns null on any failure so
 * the PDF export continues without the map rather than breaking.
 */
async function fetchRouteMapDataUrl(geometry: GeoJSON.LineString): Promise<string | null> {
	try {
		// Pre-downsample the (potentially 80k-point) geometry so the request
		// stays well under the endpoint's size cap; the server thins it further.
		const slimGeometry: GeoJSON.LineString = {
			type: 'LineString',
			coordinates: downsampleCoords(geometry.coordinates, MAP_THUMB_POINTS),
		}
		const res = await fetch('/api/static-map', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ geometry: slimGeometry, width: 1280, height: 520 }),
		})
		if (!res.ok) return null
		const blob = await res.blob()
		return await new Promise((resolve) => {
			const reader = new FileReader()
			reader.onloadend = () => resolve(typeof reader.result === 'string' ? reader.result : null)
			reader.onerror = () => resolve(null)
			reader.readAsDataURL(blob)
		})
	} catch {
		return null
	}
}

/**
 * Draw the generator/reefer line under the per-state rows (if the add-on is
 * on) and return the next y position.
 */
function drawAuxRow(
	doc: { text: (text: string, x: number, y: number, opts?: { align: 'right' }) => unknown },
	aux: AuxPowerLine | null,
	yPos: number,
	x: { label: number; gallons: number; cost: number },
): number {
	if (!aux) return yPos
	const drive = Math.round(aux.driveHours * 10) / 10
	const parked = aux.idleHours > 0 ? ` + ${Math.round(aux.idleHours * 10) / 10} h parked` : ''
	doc.text(
		`${aux.label} (${drive} h drive${parked} x ${aux.gallonsPerHour} gal/hr)`,
		x.label,
		yPos + 4,
	)
	doc.text(`${aux.gallons}`, x.gallons, yPos + 4, { align: 'right' })
	doc.text(`$${aux.cost.toFixed(2)}`, x.cost, yPos + 4, { align: 'right' })
	return yPos + 7
}

export function PdfExport({ estimate, originName, destinationName }: PdfExportProps) {
	const [isGenerating, setIsGenerating] = useState(false)
	// Generator/reefer add-on — same numbers the cost summary shows.
	const aux = useAuxPowerLine()
	const totalCost = estimate.totalCost + (aux?.cost ?? 0)
	const totalGallons = Math.round((estimate.totalGallons + (aux?.gallons ?? 0)) * 10) / 10

	const handleExport = async () => {
		setIsGenerating(true)
		try {
			const { jsPDF } = await import('jspdf')
			const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'letter' })

			const pageW = doc.internal.pageSize.getWidth()
			let yPos = 20

			// Header — CLB purple bar (#7E5FC7: white text clears 4.5:1)
			doc.setFillColor(126, 95, 199)
			doc.rect(0, 0, pageW, 12, 'F')
			doc.setTextColor(255, 255, 255)
			doc.setFontSize(14)
			doc.text('Fuel Estimate', 15, 8)

			// Date
			doc.setTextColor(150, 150, 150)
			doc.setFontSize(8)
			doc.text(new Date().toLocaleDateString('en-US', { dateStyle: 'long' }), pageW - 15, 8, {
				align: 'right',
			})

			yPos = 22

			// Route summary
			doc.setTextColor(50, 50, 50)
			doc.setFontSize(11)
			doc.text(`${originName}  →  ${destinationName}`, 15, yPos)
			yPos += 8

			// Vehicle info — flag custom-MPG override so the PDF is honest about
			// what numbers went into the estimate (user's override vs preset)
			const isCustomMpg = estimate.baseMpg !== estimate.presetMpg
			const baseMpgLabel = isCustomMpg
				? `Base MPG: ${estimate.baseMpg} (custom, preset ${estimate.presetMpg})`
				: `Base MPG: ${estimate.baseMpg}`
			doc.setFontSize(9)
			doc.setTextColor(100, 100, 100)
			doc.text(
				`Vehicle: ${estimate.vehicleName}  |  ${baseMpgLabel}  |  Fuel: ${estimate.fuelType.toUpperCase()}  |  Effective MPG: ${estimate.averageEffectiveMpg}`,
				15,
				yPos,
			)
			yPos += 10

			// Route map image — fetched fire-and-forget from our static-map proxy.
			// Adds a strong visual touch to the PDF that previously lacked any map.
			// Silent skip on failure — the rest of the PDF is more important than
			// blocking export on a Mapbox hiccup.
			if (estimate.routeGeometry) {
				const mapDataUrl = await fetchRouteMapDataUrl(estimate.routeGeometry)
				if (mapDataUrl) {
					const mapW = pageW - 30 // full content width
					const mapH = mapW * (520 / 1280) // preserve aspect ratio from request
					doc.addImage(mapDataUrl, 'PNG', 15, yPos, mapW, mapH)
					yPos += mapH + 6
				}
			}

			// Summary boxes
			const boxes = [
				{ label: 'TOTAL COST', value: `$${totalCost.toFixed(2)}` },
				{ label: 'DISTANCE', value: `${estimate.totalDistanceMiles.toLocaleString()} mi` },
				{ label: 'GALLONS', value: `${totalGallons}` },
				{
					label: 'DRIVE TIME',
					value: `${Math.floor(estimate.totalDurationMinutes / 60)}h ${estimate.totalDurationMinutes % 60}m`,
				},
			]

			const boxW = (pageW - 30 - 15) / 4
			for (let i = 0; i < boxes.length; i++) {
				const box = boxes[i] as { label: string; value: string }
				const bx = 15 + i * (boxW + 5)
				doc.setFillColor(245, 245, 245)
				doc.roundedRect(bx, yPos, boxW, 18, 2, 2, 'F')
				doc.setFontSize(7)
				doc.setTextColor(150, 150, 150)
				doc.text(box.label, bx + boxW / 2, yPos + 6, { align: 'center' })
				doc.setFontSize(12)
				doc.setTextColor(30, 30, 30)
				doc.text(box.value, bx + boxW / 2, yPos + 14, { align: 'center' })
			}
			yPos += 28

			// State breakdown table
			doc.setFontSize(9)
			doc.setTextColor(50, 50, 50)
			doc.text('Per-State Breakdown', 15, yPos)
			yPos += 5

			// Table header
			const c0 = 15,
				c1 = 55,
				c2 = 85,
				c3 = 110,
				c4 = 140,
				c5 = 170
			doc.setFillColor(240, 240, 240)
			doc.rect(15, yPos, pageW - 30, 7, 'F')
			doc.setFontSize(7)
			doc.setTextColor(120, 120, 120)
			doc.text('STATE', c0 + 2, yPos + 5)
			doc.text('MILES', c1, yPos + 5, { align: 'right' })
			doc.text('$/GAL', c2, yPos + 5, { align: 'right' })
			doc.text('GALLONS', c3, yPos + 5, { align: 'right' })
			doc.text('EFF MPG', c4, yPos + 5, { align: 'right' })
			doc.text('COST', c5, yPos + 5, { align: 'right' })
			yPos += 9

			// Table rows
			doc.setTextColor(50, 50, 50)
			doc.setFontSize(8)
			for (const seg of estimate.stateBreakdown) {
				doc.text(stateDisplayName(seg.stateCode), c0 + 2, yPos + 4)
				doc.text(`${seg.distanceMiles}`, c1, yPos + 4, { align: 'right' })
				doc.text(`$${seg.fuelPrice.toFixed(2)}`, c2, yPos + 4, { align: 'right' })
				doc.text(`${seg.gallons}`, c3, yPos + 4, { align: 'right' })
				doc.text(`${seg.effectiveMpg}`, c4, yPos + 4, { align: 'right' })
				doc.text(`$${seg.cost.toFixed(2)}`, c5, yPos + 4, { align: 'right' })
				yPos += 7
			}

			// Generator/reefer row — its own line so the add-on is visible
			yPos = drawAuxRow(doc, aux, yPos, { label: c0 + 2, gallons: c3, cost: c5 })

			// Total row
			doc.setFillColor(240, 240, 240)
			doc.rect(15, yPos, pageW - 30, 7, 'F')
			doc.setFontSize(8)
			doc.text('TOTAL', c0 + 2, yPos + 5)
			doc.text(`${Math.round(estimate.totalDistanceMiles)}`, c1, yPos + 5, { align: 'right' })
			doc.text(`${totalGallons}`, c3, yPos + 5, { align: 'right' })
			doc.setTextColor(220, 0, 0)
			doc.text(`$${totalCost.toFixed(2)}`, c5, yPos + 5, { align: 'right' })

			yPos += 15

			// Footer — provenance + legal disclaimer. The disclaimer is
			// mandatory here because the PDF is the artifact that gets
			// attached to tour budgets, invoices, and freight quotes —
			// exactly the context where a reader could mistake our
			// estimate for a commitment. Always include the terms URL.
			const canonical = canonicalUrl().replace('https://', '')
			const pageH = doc.internal.pageSize.getHeight()
			doc.setTextColor(180, 180, 180)
			doc.setFontSize(7)
			doc.text(
				`Estimate only — see ${canonical}/terms. Numbers are not a guarantee and are not financial advice.`,
				pageW / 2,
				pageH - 14,
				{ align: 'center' },
			)
			doc.text(`Generated by Fuel Estimator — ${canonical}`, pageW / 2, pageH - 10, {
				align: 'center',
			})

			// Save
			const fileName =
				`fuel-estimate-${originName.split(',')[0]}-to-${destinationName.split(',')[0]}.pdf`
					.toLowerCase()
					.replace(/\s+/g, '-')
			doc.save(fileName)
			toast.success('PDF downloaded')
			track('ExportedPDF', {
				vehicle: estimate.vehicleName,
				fuel_type: estimate.fuelType,
				distance_miles: Math.round(estimate.totalDistanceMiles),
			})
		} catch {
			toast.error('Failed to generate PDF')
		} finally {
			setIsGenerating(false)
		}
	}

	return (
		<Button variant="outline" size="sm" onClick={handleExport} disabled={isGenerating}>
			{isGenerating ? (
				<Loader2 className="size-3.5 animate-spin" />
			) : (
				<Download className="size-3.5" />
			)}
			{isGenerating ? 'Generating...' : 'Export PDF'}
		</Button>
	)
}
